import type { ShadowPlanDriver } from '../../services/shadowPlans.api';
import { formatTimeBaghdad } from '../../utils/date';
import { cn } from '../../utils/cn';
import { KIND_COLOR, KIND_LABEL, VAN_LABEL } from './shadowPlanView';

const AXIS_START_HOUR = 7; // Baghdad; op day runs 07:00 to 02:00 next day
const AXIS_HOURS = 19;
const TOTAL_WIDTH = AXIS_HOURS * 60; // 1px per minute
const ROW_HEIGHT = 40;
const NAME_WIDTH = 160; // w-40
const BAR_MIN = 13;
const HOURS = Array.from({ length: AXIS_HOURS }, (_, i) => (AXIS_START_HOUR + i) % 24);

interface ShadowTimelineProps {
  drivers: (ShadowPlanDriver & { color?: string })[];
  opDate: string;
  selectedLabel?: string;
  onSelect?: (label: string) => void;
}

export default function ShadowTimeline({ drivers, opDate, selectedLabel, onSelect }: ShadowTimelineProps) {
  const axisStart = Date.parse(`${opDate}T${String(AXIS_START_HOUR).padStart(2, '0')}:00:00+03:00`);
  const minutesIn = (iso: string) => (Date.parse(iso) - axisStart) / 60000;

  return (
    <div className="border border-gray-200 rounded-lg overflow-x-auto bg-white">
      <div className="flex" style={{ width: NAME_WIDTH + TOTAL_WIDTH }}>
        <div className="w-40 flex-shrink-0 sticky left-0 z-10 bg-gray-50 border-r border-gray-200 px-3 py-2 text-xs font-medium text-gray-500 uppercase">
          Driver
        </div>
        <div>
          <div className="flex" style={{ width: TOTAL_WIDTH }}>
            {HOURS.map((h, i) => (
              <div key={i} className="text-[10px] text-gray-500 border-l border-gray-200 px-0.5 py-2 tabular-nums" style={{ width: 60 }}>
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>
        </div>
      </div>

      {drivers.map((d) => (
        <div
          key={d.label}
          className={cn('flex border-t border-gray-100', selectedLabel === d.label && 'bg-blue-50')}
          style={{ width: NAME_WIDTH + TOTAL_WIDTH }}
        >
          <button
            type="button"
            onClick={() => onSelect?.(d.label)}
            aria-pressed={selectedLabel === d.label}
            className="w-40 flex-shrink-0 sticky left-0 z-10 bg-white border-r border-gray-200 px-3 text-left text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ height: ROW_HEIGHT }}
          >
            <span className="flex items-center gap-1.5">
              {d.color && <span aria-hidden className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: d.color }} />}
              <span>Driver {d.label}</span>
            </span>
            <span className="block text-[11px] font-normal text-gray-500">
              {VAN_LABEL[d.vehicleType]} · {d.stops.length} stops
            </span>
          </button>
          <div>
            <div className="relative" style={{ width: TOTAL_WIDTH, height: ROW_HEIGHT }}>
              {HOURS.map((_, i) => (
                <div key={i} className="absolute top-0 bottom-0 border-l border-gray-100" style={{ left: i * 60 }} />
              ))}
              {d.stops.map((s) => {
                const label = `${s.merchantName ?? KIND_LABEL[s.kind]} · ${formatTimeBaghdad(s.eta)} · ${s.orders} orders${s.isCold ? ' · cold' : ''}${s.isForecast ? ' · expected' : ''}`;
                return (
                  <div
                    key={s.seq}
                    data-testid="shadow-stop"
                    role="img"
                    aria-label={label}
                    title={label}
                    className={cn(
                      'absolute top-1.5 bottom-1.5 rounded border',
                      s.isForecast ? 'bg-gray-200 border-gray-400 border-dashed' : KIND_COLOR[s.kind]
                    )}
                    style={{ left: minutesIn(s.eta), width: BAR_MIN }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** What the box colours on the timeline mean. */
export function ShadowLegend({ kinds }: { kinds?: Set<string> }) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600" aria-label="Legend">
      {(Object.keys(KIND_COLOR) as (keyof typeof KIND_COLOR)[]).filter((k) => !kinds || kinds.has(k)).map((k) => (
        <li key={k} className="flex items-center gap-1.5">
          <span aria-hidden className={cn('h-3 w-3 rounded border', KIND_COLOR[k])} />
          {KIND_LABEL[k]}
        </li>
      ))}
      <li className="flex items-center gap-1.5">
        <span aria-hidden className="h-3 w-3 rounded border border-dashed border-gray-400 bg-gray-200" />
        Expected (not booked yet)
      </li>
    </ul>
  );
}
